import posthog from 'posthog-js'

/**
 * Capture a marketing-site CTA click. Uses sendBeacon so the event survives
 * the full-page navigation most CTAs trigger (marketing host → app host).
 * The site markets every product, so the event has no product prefix; its
 * `product` property comes from the page (see productTags).
 */
export function trackMarketingCta(cta: string, properties?: Record<string, unknown>) {
  posthog.capture('marketing_cta_clicked', { cta, ...properties }, { transport: 'sendBeacon' })
}

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    dataLayer?: Record<string, unknown>[]
  }
}

/**
 * Fire a Meta Pixel standard event.
 *
 * The pixel is injected server-side into `<head>` and only in production
 * (`backend/insure/middlewares/tracking_scripts_middleware.py`), so `window.fbq` is
 * simply absent in dev, staging and preview — hence the optional call rather than a
 * guard on `isProduction`.
 *
 * `eventId` becomes Meta's `eventID`, the key it deduplicates a browser event against
 * the same event sent through the Conversions API. Nothing sends server-side events
 * today; a stable id now is what keeps adding one later additive rather than double-counting.
 */
export function trackMetaEvent(name: string, properties?: Record<string, unknown>, eventId?: string) {
  window.fbq?.('track', name, properties ?? {}, eventId ? { eventID: eventId } : undefined)
}

/** The public form a carrier entered its DOT on, named as the backend's `SalesLead.EntryForm`. */
export enum DotEntryForm {
  ESTIMATES = 'ESTIMATES',
  APPLICATION_START = 'APPLICATION_START',
  LARGE_FLEETS = 'LARGE_FLEETS',
}

/**
 * Tell Google Tag Manager a DOT resolved to a real carrier, at the same moments the
 * backend records `insure_lead_dot_entered`. Tags and triggers built on `dot_entered`
 * live in GTM, so marketing can change them without a deploy.
 *
 * `dot_number` doubles as the conversion's transaction id: Google drops a repeat of
 * the same DOT, matching the backend's once-per-carrier count.
 */
export function trackDotEntered(dotNumber: string, entryForm: DotEntryForm) {
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({ event: 'dot_entered', dot_number: dotNumber, entry_form: entryForm })
}

/**
 * Tell PostHog which org the signed-in user is acting for.
 *
 * `identify` alone only ever produces person-level analytics — org cohorts,
 * per-org funnels and the group filters in PostHog's own UI all need a group
 * association, which is what `group()` creates. Safe to call on every render
 * pass: PostHog only emits a $groupidentify when the properties actually change.
 */
export function identifyOrg(
  org: { id: string; name: string; status?: string; demoMode?: boolean } | null | undefined
) {
  if (!org) return
  posthog.group('org', org.id, {
    name: org.name,
    status: org.status,
    demo_mode: org.demoMode,
  })
}

/**
 * Clear the identity and any group association. Must run before sign-out —
 * PostHog persists both in local storage, so without this the next person to
 * sign in on the same browser inherits the previous user's org.
 */
export function resetAnalytics() {
  posthog.reset()
}
